import { FC, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CardHeader } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { FormHandles, SubmitHandler } from '@unform/core';
import { Form } from '@unform/web';
import BackButton from 'src/components/BackButton';
import Button from 'src/components/Button';
import ConfirmDialog from 'src/components/ConfirmDialog';
import { ConfirmDialogRef } from 'src/components/ConfirmDialog/interfaces';
import Autocomplete from 'src/components/Form/Autocomplete';
import DateTimePicker from 'src/components/Form/DateTimePicker';
import InputFile from 'src/components/Form/InputFile';
import RichText from 'src/components/Form/RichText';
import TextField from 'src/components/Form/TextField';
import { IEditParams } from 'src/interfaces/IEditParams';
import { IPartner, IService } from 'src/interfaces/models';
import IOffer from 'src/interfaces/models/IOffer';
import PrivateContext from 'src/routes/Private/PrivateContext';
import api from 'src/services/api';
import { handleApiResponseErrors, showFormErrors } from 'src/utils/errors';
import { objectToFormData, objectToQuery } from 'src/utils/helpers';
import yupValidate from 'src/utils/yupValidate';
import {
  OfferStoreSchema,
  OfferUpdateSchema,
} from 'src/validators/Offer/save.schema';

import { Container } from './styles';

const Save: FC = () => {
  const { startLayoutLoading, stopLayoutLoading } = useContext(PrivateContext);
  const formRef = useRef<FormHandles>(null);
  const confirmDialogRef = useRef<ConfirmDialogRef>(null);
  const [loading, setLoading] = useState(false);
  const [offer, setOffer] = useState<IOffer>();
  const [partners, setPartners] = useState<IPartner[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<IPartner>();
  const [services, setServices] = useState<IService[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const history = useHistory();
  const params = useParams<IEditParams>();

  const yupSchema = params.id ? OfferUpdateSchema : OfferStoreSchema;

  useEffect(() => {
    const loadData = async () => {
      try {
        startLayoutLoading();

        const queryParams = objectToQuery({
          // benefit_type: 'bonus',
          order_by: [
            { column: 'status', direction: 'asc' },
            { column: 'display_name', direction: 'asc' },
          ],
        });
        const { data } = await api.get<IPartner[]>(
          `admin/partners${queryParams}`,
        );
        setPartners(data);

        if (params.id) {
          const { data } = await api.get<IOffer>(`admin/offers/${params.id}`);
          setOffer(data);
          formRef.current?.setData(data);
        }
      } catch (error) {
        handleApiResponseErrors(error.response, 'Erro ao buscar dados.');
      } finally {
        stopLayoutLoading();
      }
    };

    loadData();
  }, [params.id, startLayoutLoading, stopLayoutLoading]);

  useEffect(() => {
    async function handlePartnerChange() {
      try {
        if (!selectedPartner) {
          setServices([]);
          return;
        }

        const queryParams = objectToQuery({
          load_category: true,
          order_by: [{ column: 'name', direction: 'asc' }],
        });

        const { data } = await api.get<IService[]>(
          `admin/services${queryParams}`,
        );
        setServices(data);

        if (offer) {
          setTimeout(() => {
            formRef.current?.setFieldValue('service_id', offer.service_id);
          }, 200);
        }
      } catch (error) {
        setServices([]);
      }
    }

    handlePartnerChange();
  }, [selectedPartner, offer]);

  const handleOnSubmit: SubmitHandler = async (unformData) => {
    try {
      setLoading(true);
      formRef.current?.setErrors({});

      const { success, data, errors } = await yupValidate(
        yupSchema,
        unformData,
      );

      if (!success) {
        return showFormErrors(errors, formRef);
      }

      const formData = objectToFormData(data);

      if (params.id) {
        await api.put(`/admin/offers/${params.id}`, formData);
      } else {
        await api.post('/admin/offers', formData);
      }

      toast.success('Dados salvos com sucesso!');
      history.goBack();
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro', yupSchema, formRef);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);

      await api.delete(`/admin/offers/${params.id}`);
      toast.success('Oferta excluida!');
      history.goBack();
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro ao excluir oferta.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Form ref={formRef} onSubmit={handleOnSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container alignItems="center">
              <BackButton />

              <Typography variant="h5">
                {params.id ? 'Editar' : 'Nova'} Oferta
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField name="name" label="Título da Ofeta" required />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <DateTimePicker
                      name="start_at"
                      label="Início da Oferta"
                      valueAsDate
                      onChange={(value) => setStartDate(value)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <DateTimePicker
                      name="end_at"
                      label="Fim da Oferta"
                      valueAsDate
                      minDate={startDate}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="textSecondary">
                      Descrição Curta da Oferta
                    </Typography>

                    <RichText name="description" />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="textSecondary">
                      Descrição Complementar da Oferta
                    </Typography>

                    <RichText name="details" />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField name="link" label="Link" required />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <InputFile
                      name="image"
                      label="Imagem"
                      required
                      previewAlt="Imagem"
                      canDelete
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardHeader subheader="Vínculo da Oferta" />

              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Autocomplete
                      name="partner_id"
                      label="Parceiro"
                      options={partners.map((partner) => ({
                        key: partner.id,
                        label: `${partner.display_name}`,
                        value: partner.id,
                        disabled: partner.status !== 'active',
                        data: partner,
                      }))}
                      textFieldProps={{ required: true }}
                      onChange={(value) => {
                        setSelectedPartner(value?.data);
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      name="service_id"
                      label="Serviço (Categoria)"
                      options={services.map((service) => ({
                        key: service.id,
                        label: `${service?.name} (${service.category?.name})`,
                        value: service.id,
                      }))}
                      textFieldProps={{ required: true }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Grid container justify="space-between">
              <Button
                type="button"
                loading={loading}
                startIcon={<CloseIcon />}
                variant="contained"
                color="secondary"
                disabled={!params.id}
                onClick={() => confirmDialogRef.current?.show()}
              >
                Excluir
              </Button>

              <Button
                type="submit"
                loading={loading}
                startIcon={<CheckIcon />}
                variant="contained"
                color="primary"
              >
                Salvar
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Form>

      <ConfirmDialog
        ref={confirmDialogRef}
        title="Excluir Oferta"
        description="Confirma esta ação?"
        confirmColor="secondary"
        onConfirm={handleConfirm}
      />
    </Container>
  );
};

export default Save;
